(() => {
  const BACKGROUND_DESKTOP_VAR = '--image-product-card-background';
  const BACKGROUND_MOBILE_VAR = '--image-product-card-background-mobile';
  const INIT_FLAG = 'imageProductCardInitialized';

  const initProductCards = () => {
    const articleTemplate = document.querySelector('.article-template[data-product-card-script-url]');
    if (!articleTemplate || typeof window.jQuery === 'undefined') return;

    const productContainer = window.jQuery('.shokz-product-card-wrap');
    const productItems = window.jQuery('.article-template .product_cards');
    if (!productContainer.length || !productItems.length) return;

    productContainer.empty();
    productItems.appendTo(productContainer);

    const productCardScript = document.createElement('script');
    productCardScript.src = articleTemplate.dataset.productCardScriptUrl;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(productCardScript, firstScript);
  };

  const normalizeTrackingQuery = (query) => {
    if (!query) return '';
    return String(query)
      .trim()
      .replace(/^\?+/, '')
      .replace(/&amp;/gi, '&');
  };

  const appendQueryToHref = (anchor, query) => {
    if (!anchor || !query) return;

    const normalizedQuery = normalizeTrackingQuery(query);
    if (!normalizedQuery) return;

    let incoming;
    try {
      incoming = new URLSearchParams(normalizedQuery);
    } catch (error) {
      return;
    }
    if (![...incoming.keys()].length) return;

    const rawHref = anchor.getAttribute('href') || anchor.href;
    if (!rawHref) return;

    try {
      const url = new URL(rawHref, window.location.origin);
      incoming.forEach((value, key) => {
        url.searchParams.set(key, value);
      });
      // Keep a root-relative href so Shopify / markets paths stay consistent.
      anchor.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    } catch (error) {
      // Ignore invalid hrefs; leave the original link untouched.
    }
  };

  const TRACKING_CACHE = 'iwpcTrackingQueries';
  const TRACK_SELECTOR = 'a.shokz-iwpc-track, .shokz-iwpc-track';

  const queryFromTrackHref = (href) => {
    if (!href) return '';

    const trimmed = String(href).trim();
    if (!trimmed || trimmed === '#') return '';

    // Prefer "?ref=..." / "ref=..." forms that ops paste into the article HTML.
    if (trimmed.startsWith('?') || (!trimmed.includes('://') && trimmed.includes('='))) {
      return normalizeTrackingQuery(trimmed);
    }

    try {
      const url = new URL(trimmed, window.location.origin);
      return normalizeTrackingQuery(url.search);
    } catch (error) {
      return normalizeTrackingQuery(trimmed);
    }
  };

  const collectTrackingQueries = (placeholder) => {
    if (Array.isArray(placeholder[TRACKING_CACHE])) {
      return placeholder[TRACKING_CACHE];
    }

    const fromTracks = Array.from(placeholder.querySelectorAll(TRACK_SELECTOR)).map((track) =>
      queryFromTrackHref(track.getAttribute('href') || ''),
    );

    const queries = [];
    for (let index = 0; index < 3; index += 1) {
      const attrName = `data-tracking-${index + 1}`;
      const fromData =
        placeholder.getAttribute(attrName) || placeholder.dataset[`tracking${index + 1}`] || '';
      queries.push(normalizeTrackingQuery(fromTracks[index] || fromData));
    }

    placeholder[TRACKING_CACHE] = queries;
    return queries;
  };

  const getTrackingQuery = (placeholder, index) => collectTrackingQueries(placeholder)[index] || '';

  const applyCardTracking = (card, placeholder, index) => {
    const trackingQuery = getTrackingQuery(placeholder, index);
    if (!trackingQuery) return;

    const anchor = card.matches?.('a[href]') ? card : card.querySelector?.('a[href]');
    appendQueryToHref(anchor, trackingQuery);
  };

  const applyTrackingToWrap = (wrap) => {
    collectTrackingQueries(wrap);
    const cards = wrap.querySelectorAll('.image-with-product-card-item, a.image-with-product-card-item');
    cards.forEach((card, index) => applyCardTracking(card, wrap, index));
  };

  const parseCssUrlValue = (value) => {
    if (!value || typeof value !== 'string') return '';

    const trimmed = value.trim();
    if (!trimmed || trimmed === 'none') return '';

    const matched = trimmed.match(/^url\(\s*(['"]?)(.*?)\1\s*\)$/i);
    if (matched) {
      const url = matched[2].trim();
      return isUsableImageUrl(url) ? url : '';
    }

    return isUsableImageUrl(trimmed) ? trimmed : '';
  };

  const isUsableImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    const trimmed = url.trim();
    if (!trimmed || trimmed === '#') return false;

    // Skip common 1x1 lazy-load placeholders.
    if (/^data:image\/gif;base64,R0lGOD/i.test(trimmed)) return false;

    return true;
  };

  const pickSrcsetUrl = (srcset) => {
    if (!srcset) return '';

    const candidates = srcset
      .split(',')
      .map((part) => {
        const bits = part.trim().split(/\s+/);
        return {
          url: bits[0] || '',
          width: Number.parseInt(bits[1], 10) || 0,
        };
      })
      .filter((candidate) => isUsableImageUrl(candidate.url));

    if (!candidates.length) return '';

    candidates.sort((a, b) => b.width - a.width);
    return candidates[0].url;
  };

  const resolveImageElementUrl = (element) => {
    if (!element) return '';

    if (element.tagName === 'PICTURE') {
      const img = element.querySelector('img');
      const fromImg = resolveImageElementUrl(img);
      if (fromImg) return fromImg;

      const sources = element.querySelectorAll('source[srcset]');
      for (const source of sources) {
        const fromSrcset = pickSrcsetUrl(source.getAttribute('srcset'));
        if (fromSrcset) return fromSrcset;
      }

      return '';
    }

    if (element.tagName !== 'IMG') return '';

    if (isUsableImageUrl(element.currentSrc)) return element.currentSrc.trim();
    if (isUsableImageUrl(element.src)) return element.src.trim();

    return pickSrcsetUrl(element.getAttribute('srcset'));
  };

  const isProductCardImage = (element) =>
    Boolean(element?.closest('.image-with-product-card-list, .image-with-product-card-item'));

  const findBackgroundImageElement = (placeholder) => {
    if (!placeholder) return null;

    const pictures = placeholder.querySelectorAll('picture');
    for (const picture of pictures) {
      if (!isProductCardImage(picture)) return picture;
    }

    const images = placeholder.querySelectorAll('img');
    for (const image of images) {
      if (!isProductCardImage(image)) return image;
    }

    return null;
  };

  const cssBackgroundValue = (url) => `url(${JSON.stringify(url)})`;

  const applyBackgroundStyles = (placeholder, desktopUrl, mobileUrl) => {
    if (isUsableImageUrl(desktopUrl)) {
      placeholder.setAttribute('data-background-image', desktopUrl);
      placeholder.style.setProperty(BACKGROUND_DESKTOP_VAR, cssBackgroundValue(desktopUrl));
    }

    const resolvedMobile = isUsableImageUrl(mobileUrl) ? mobileUrl : desktopUrl;
    if (isUsableImageUrl(resolvedMobile)) {
      placeholder.setAttribute('data-background-image-mobile', resolvedMobile);
      placeholder.style.setProperty(BACKGROUND_MOBILE_VAR, cssBackgroundValue(resolvedMobile));
    }

    if (placeholder.dataset.backgroundPosition) {
      placeholder.style.setProperty('--image-product-card-position', placeholder.dataset.backgroundPosition);
    }

    if (placeholder.dataset.backgroundPositionMobile) {
      placeholder.style.setProperty(
        '--image-product-card-position-mobile',
        placeholder.dataset.backgroundPositionMobile,
      );
    }
  };

  const resolveBackgroundUrls = (placeholder) => {
    // Prefer inline CSS variables (survives Shopify RTE better than data-* attrs).
    const desktopFromStyle = parseCssUrlValue(placeholder.style.getPropertyValue(BACKGROUND_DESKTOP_VAR));
    const mobileFromStyle = parseCssUrlValue(placeholder.style.getPropertyValue(BACKGROUND_MOBILE_VAR));

    const desktopFromData =
      placeholder.getAttribute('data-background-image') || placeholder.dataset.backgroundImage || '';
    const mobileFromData =
      placeholder.getAttribute('data-background-image-mobile') ||
      placeholder.dataset.backgroundImageMobile ||
      '';

    let desktop =
      desktopFromStyle || (isUsableImageUrl(desktopFromData) ? desktopFromData.trim() : '');
    let mobile = mobileFromStyle || (isUsableImageUrl(mobileFromData) ? mobileFromData.trim() : '');

    if (!desktop) {
      desktop = resolveImageElementUrl(findBackgroundImageElement(placeholder));
    }

    if (!mobile) {
      mobile = desktop;
    }

    return { desktop, mobile };
  };

  const preloadBackground = (url) => {
    if (!isUsableImageUrl(url)) return;

    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    image.onerror = () => {
      console.warn('[ImageWithProductCard] Background failed to load:', url);
    };
  };

  const bootstrapBackgroundsEarly = () => {
    document.querySelectorAll('.shokz-image-with-product-card-wrap').forEach((placeholder) => {
      const { desktop, mobile } = resolveBackgroundUrls(placeholder);
      applyBackgroundStyles(placeholder, desktop, mobile);
    });
  };

  const initImageWithProductCards = () => {
    const articleTemplate = document.querySelector('.article-template');
    const productSource = articleTemplate?.querySelector('[data-image-with-product-card-products]');
    const placeholders = articleTemplate?.querySelectorAll('.shokz-image-with-product-card-wrap');
    if (!placeholders?.length) return;

    const productCards = productSource ? Array.from(productSource.children).slice(0, 3) : [];

    const populatePlaceholder = (placeholder) => {
      // Cache tracking before DOM rewrite removes .shokz-iwpc-track nodes.
      collectTrackingQueries(placeholder);

      if (
        placeholder.dataset[INIT_FLAG] === 'true' &&
        placeholder.classList.contains('shokz-image-with-product-card-wrap--ready')
      ) {
        const { desktop, mobile } = resolveBackgroundUrls(placeholder);
        applyBackgroundStyles(placeholder, desktop, mobile);
        applyTrackingToWrap(placeholder);
        return;
      }

      const { desktop, mobile } = resolveBackgroundUrls(placeholder);
      applyBackgroundStyles(placeholder, desktop, mobile);

      if (desktop) {
        preloadBackground(desktop);
      } else {
        console.warn('[ImageWithProductCard] Missing background image for placeholder.', placeholder);
      }

      if (productCards.length) {
        const cardsContainer = document.createElement('div');
        cardsContainer.className = 'image-with-product-card-list';
        cardsContainer.dataset.productCount = String(productCards.length);
        productCards.forEach((card, index) => {
          const clonedCard = card.cloneNode(true);
          applyCardTracking(clonedCard, placeholder, index);
          cardsContainer.append(clonedCard);
        });

        placeholder.replaceChildren(cardsContainer);
      }

      applyTrackingToWrap(placeholder);
      placeholder.dataset[INIT_FLAG] = 'true';
      placeholder.classList.add('shokz-image-with-product-card-wrap--ready');
    };

    placeholders.forEach((placeholder) => populatePlaceholder(placeholder));
    productSource?.remove();
  };

  const initArticleBlogFilter = () => {
    document.querySelectorAll('[data-article-blog-filter]').forEach((link) => {
      link.addEventListener('click', () => {
        console.info('[ArticleBlogFilter][1] Tag clicked', {
          currentUrl: window.location.href,
          filterUrl: link.dataset.articleBlogFilter,
          cleanUrl: link.dataset.articleBlogUrl,
          href: link.href,
        });

        try {
          sessionStorage.setItem(
            'shokz:pending-blog-filter',
            JSON.stringify({
              blogUrl: link.dataset.articleBlogUrl,
              filterUrl: link.dataset.articleBlogFilter,
            }),
          );
          console.info('[ArticleBlogFilter][2] Navigation state saved; browser will request tagged SSR', {
            storage: sessionStorage.getItem('shokz:pending-blog-filter'),
            nextUrl: link.href,
          });
        } catch (error) {
          console.warn(
            '[ArticleBlogFilter][2] Unable to save navigation state; tagged navigation will continue',
            error,
          );
        }
      });
    });
  };

  const initLegacyBlogNav = () => {
    if (typeof window.jQuery === 'undefined') return;

    window.jQuery('.blog-nav li').on('click', function () {
      const dataIndex = window.jQuery(this).attr('data-index');
      window.jQuery('html,body').animate(
        {
          scrollTop: window.jQuery(`.for-${dataIndex}`).offset().top - 120,
        },
        300,
      );
    });
  };

  const initArticleToc = () => {
    if (typeof window.jQuery === 'undefined') return;

    const content = window.jQuery('.article-wrapper .left-wrapper');
    const nav = window.jQuery('#article-nav-ho .article-toc-ho');
    const headings = content.find('h2');
    if (!headings.length) return;

    window.jQuery('#article-nav-ho').show();

    headings.each(function () {
      const heading = window.jQuery(this);
      const text = window.jQuery.trim(heading.text());
      if (!text) return;

      nav.append(window.jQuery('<li></li>').text(text).data('target', heading));
    });

    nav.css('--article-toc-row-count', Math.ceil(nav.children('li').length / 2));

    nav.on('click', 'span,li', function () {
      const target = window.jQuery(this).data('target');
      if (!target || !target.length) return;

      window.jQuery('html, body').animate(
        {
          scrollTop: target.offset().top - 150,
        },
        400,
      );
    });
  };

  const init = () => {
    initProductCards();
    initImageWithProductCards();
    initArticleBlogFilter();
    initLegacyBlogNav();
    initArticleToc();
  };

  // Apply background CSS vars as soon as the deferred script runs (reduces gray FOUC).
  bootstrapBackgroundsEarly();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
